import { useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { InputLabelHeight } from "@covertree/types";
import { FormSelectField, FormToggleCard, HelpText } from "@covertree/ui";

import { getUnitCoverageFormPath as fieldName } from "@redux/slices/endorsement/extraCoverageFields";

import {
  isDisabledValue,
  isExtraCoverageOn,
  type EndorsementExtraCoverage,
  type ExtraCoverageValue,
} from "./extraCoverages";
import { ExtraCoverageItems } from "./ExtraCoverageItems";

export const NO_ITEMS_ERROR = "At least one item is required for this coverage";

const requireItems = (value: ExtraCoverageValue) =>
  !Array.isArray(value) || value.length > 0 || NO_ITEMS_ERROR;

function getExtraCoverageDetails(
  coverage: EndorsementExtraCoverage,
  value: ExtraCoverageValue,
  onChange: (value: ExtraCoverageValue) => void,
  control: ReturnType<typeof useFormContext>["control"],
  viewOnly: boolean,
  error?: string
) {
  if (coverage.fieldGroup) {
    return (
      <ExtraCoverageItems
        fieldGroup={coverage.fieldGroup}
        items={Array.isArray(value) ? value : []}
        isCustomizable={coverage.isCustomizable && !viewOnly}
        error={error}
        onChange={onChange}
        onDisable={() => onChange(isDisabledValue(coverage))}
      />
    );
  }

  if (coverage.options.length > 0) {
    return (
      <Box width={1}>
        <FormSelectField
          id={`${coverage.key}-value`}
          dataTestId={`${coverage.key}-value`}
          name={fieldName(coverage.key)}
          control={control}
          label={coverage.getValueFormats(value)?.prefix}
          labelHeight={InputLabelHeight.SMALL}
          options={coverage.options}
          disabled={viewOnly || !coverage.isCustomizable}
        />
      </Box>
    );
  }

  const { prefix, value: formatted } = coverage.getValueFormats(value) ?? {};

  return prefix && formatted ? (
    <HelpText text={prefix} spanText={formatted} />
  ) : null;
}

function ExtraCoverageCard({
  coverage,
  viewOnly,
}: Readonly<{ coverage: EndorsementExtraCoverage; viewOnly: boolean }>) {
  const { control } = useFormContext();
  // The form validates on every change (`mode: "all"`), so the error is shown
  // only once Continue was pressed after this coverage last changed.
  const submitCountAtChange = useRef(0);

  return (
    <Controller
      control={control}
      name={fieldName(coverage.key)}
      defaultValue={coverage.value}
      rules={
        coverage.fieldGroup && !viewOnly
          ? { validate: requireItems }
          : undefined
      }
      render={({ field: { value, onChange }, fieldState, formState }) => {
        const isOn = isExtraCoverageOn(value);
        submitCountAtChange.current = Math.min(
          submitCountAtChange.current,
          formState.submitCount
        );
        const error =
          formState.submitCount > submitCountAtChange.current
            ? fieldState.error?.message
            : undefined;
        const change = (next: ExtraCoverageValue) => {
          submitCountAtChange.current = formState.submitCount;
          onChange(next);
        };

        return (
          <FormToggleCard
            title={coverage.name}
            subheader={coverage.description}
            tooltip={coverage.tooltip}
            image={coverage.image}
            checked={isOn}
            checkedCustomStyles={{ backgroundColor: "secondary.contrastText" }}
            disabled={viewOnly || !coverage.isCustomizable}
            onToggle={() =>
              change(
                isOn ? isDisabledValue(coverage) : coverage.valueWhenEnabled
              )
            }
            actions={
              isOn
                ? getExtraCoverageDetails(
                    coverage,
                    value,
                    change,
                    control,
                    viewOnly,
                    error
                  )
                : null
            }
          />
        );
      }}
    />
  );
}

export function EndorsementExtraCoveragesCustomization({
  coverages,
  viewOnly = false,
}: Readonly<{
  coverages: EndorsementExtraCoverage[];
  viewOnly?: boolean;
}>) {
  return (
    <Stack gap={2}>
      {coverages.map((coverage) => (
        <ExtraCoverageCard
          key={coverage.key}
          coverage={coverage}
          viewOnly={viewOnly}
        />
      ))}
    </Stack>
  );
}
